import React from "react";
import { FormattedDate } from "react-intl";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";

import {
  styled,
  Fab,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  CircularProgress,
  alpha,
} from "@mui/material";
import {
  Pin as PinIcon,
  PinOff as UnpinIcon,
  Paw as LapidouIcon,
} from "mdi-material-ui";

import CreationEvent from "./Creation";
import CheckoutEvent from "./Checkout";
import PaymentIntentEvent from "./PaymentIntent";
import StateChangeEvent from "./StateChange";
import CommentEvent from "./Comment";
import PhoneCallEvent from "./PhoneCall";
import SoundShapeEvent from "./SoundShape";
import SoundShapeRemoveEvent from "./SoundShapeRemove";
import FeedbackEvent from "./Feedback";
import FileEvent from "./File";
import MailEvent from "./Mail";
import ShipmentEvent from "./Shipment";
import ReturnEvent from "./Return";

const components = {
  creation: CreationEvent,
  checkout: CheckoutEvent,
  paymentIntent: PaymentIntentEvent,
  stateChange: StateChangeEvent,
  comment: CommentEvent,
  phoneCall: PhoneCallEvent,
  soundShape: SoundShapeEvent,
  soundShapeRemove: SoundShapeRemoveEvent,
  feedback: FeedbackEvent,
  file: FileEvent,
  mail: MailEvent,
  shipment: ShipmentEvent,
  return: ReturnEvent,
};

const DefaultEvent = {
  Icon: () => null,
  Title: ({ orderEvent }) => <span>{orderEvent.type}</span>,
  Content: ({ orderEvent }) => <pre>{JSON.stringify(orderEvent, null, 2)}</pre>,
};

const MUTATION = gql`
  mutation ($orderEventId: ID!, $pinned: Boolean!) {
    PinOrderEvent(orderEventId: $orderEventId, pinned: $pinned) {
      event {
        id
        pinned
      }
    }
  }
`;

// const should
const shouldForwardProp = (prop) => prop !== "pinned";
const CardRoot = styled(Card, { shouldForwardProp })(({ pinned, theme }) => ({
  backgroundColor: pinned && alpha(theme.palette.error.main, 0.2),
}));

export default function OrderEvent({ orderEvent, ...props }) {
  const userProfile = (orderEvent.user && orderEvent.user.profile) || {
    email: null,
    name: "Lapidou",
    pictureComponent: <LapidouIcon />,
  };

  const base = components[orderEvent.type] || DefaultEvent;

  const ContentComponent = base.Content;
  const TitleComponent = base.Title;

  const [togglePin, { loading }] = useMutation(MUTATION, {
    variables: {
      orderEventId: orderEvent.id,
      pinned: !orderEvent.pinned,
    },
  });

  return (
    <CardRoot pinned={orderEvent.pinned} {...props}>
      <CardHeader
        avatar={
          <Avatar
            src={userProfile.picture}
            children={userProfile.pictureComponent}
          />
        }
        action={
          <Fab
            disabled={loading}
            onClick={togglePin}
            size="small"
            sx={{
              transition: (t) => t.transitions.create("opacity"),
              opacity: 0,
              [`${CardRoot}:hover &`]: { opacity: 1 },
            }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : orderEvent.pinned ? (
              <UnpinIcon />
            ) : (
              <PinIcon />
            )}
          </Fab>
        }
        title={<TitleComponent orderEvent={orderEvent} />}
        subheader={
          <small>
            {userProfile.fullName} {userProfile.email}
            {" - "}
            <FormattedDate
              value={orderEvent.createdAt}
              year="numeric"
              month="long"
              day="2-digit"
              hour="2-digit"
              minute="2-digit"
            />
          </small>
        }
      />

      {ContentComponent && (
        <CardContent>
          <ContentComponent orderEvent={orderEvent} />
        </CardContent>
      )}
    </CardRoot>
  );
}

OrderEvent.Icon = ({ orderEvent }) => {
  const Component = components[orderEvent.type] || DefaultEvent;
  const IconComponent = Component.Icon || DefaultEvent.Icon;

  return <IconComponent orderEvent={orderEvent} />;
};

OrderEvent.fragments = {
  orderEvent: gql`
    fragment Orders_OrderEvent_OrderEvent on OrderEvent {
      id
      createdAt
      type
      pinned

      user {
        id

        profile {
          email
          fullName
          picture
        }
      }

      ...Orders_OrderEvent_StateChangeEvent_OrderEvent
      ...Orders_OrderEvent_CommentEvent_OrderEvent
      ...Orders_OrderEvent_PhoneCallEvent_OrderEvent
      ...Orders_OrderEvent_PaymentIntentEvent_OrderEvent
      ...Orders_OrderEvent_SoundShapeEvent_OrderEvent
      ...Orders_OrderEvent_SoundShapeRemoveEvent_OrderEvent
      ...Orders_OrderEvent_FeedbackEvent_OrderEvent
      ...Orders_OrderEvent_FileEvent_OrderEvent
      ...Orders_OrderEvent_MailEvent_OrderEvent
      ...Orders_OrderEvent_ShipmentEvent_OrderEvent
      ...Orders_OrderEvent_ReturnEvent_OrderEvent
    }

    ${StateChangeEvent.fragments.orderEvent}
    ${CommentEvent.fragments.orderEvent}
    ${PhoneCallEvent.fragments.orderEvent}
    ${PaymentIntentEvent.fragments.orderEvent}
    ${SoundShapeEvent.fragments.orderEvent}
    ${SoundShapeRemoveEvent.fragments.orderEvent}
    ${FeedbackEvent.fragments.orderEvent}
    ${FileEvent.fragments.orderEvent}
    ${MailEvent.fragments.orderEvent}
    ${ShipmentEvent.fragments.orderEvent}
    ${ReturnEvent.fragments.orderEvent}
  `,
};
